import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ReviewCardsCompatibility } from '@/components/ReviewCards';
import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { AnimationFill } from '@/pages/Onboarding/components/AnimationFill';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { LogoWithAppName } from '@/pages/Onboarding/components/LogoWithAppName';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useQueryPersonalizedAis } from '@/pages/Onboarding/hooks/useQueryPersonalizedAis';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { ScreenSettingUpProfileStyles } from './SettingUpProfile.styles';

import type { LayoutDataType } from '../../types';

const getAnimationSteps = (gender: LayoutDataType['gender']) => {
  const genderLabel = gender === 'female' ? 'girlfriend' : 'boyfriend';

  return [
    {
      delay: 0,
      label: 'Processing your request',
    },
    {
      delay: 4,
      label: 'Initializing AI modules',
    },
    {
      delay: 8,
      label: `Creating your AI ${genderLabel}`,
    },
  ];
};

export const ScreenSettingUpProfile: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'SettingUpProfile',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const gender = form.watch('gender');
  const animationSteps = React.useMemo(
    () => getAnimationSteps(gender),
    [gender],
  );
  const [disabledNextStep, setDisabledNextStep] = React.useState<boolean>(true);

  useQueryPersonalizedAis({
    enabled: true,
    form,
    onSuccess: (data) => {
      form.setValue('characters', data.twins);
      data.twins.forEach((twin) => preloadImage(twin.profile_picture_url));
    },
  });

  const handleNextStep = () => {
    nextStepTimeout(wizard.nextStep);
  };

  const onFinishAnimation = () => {
    setDisabledNextStep(false);
  };

  return (
    <ScreenSettingUpProfileStyles>
      <LogoWithAppName withCaption={false} />
      <OwnAiTitle className="setting-up-title">
        Setting up your profile...
      </OwnAiTitle>
      <OnboardingSubtitle className="setting-up-subtitle">
        It will take less than one minute
      </OnboardingSubtitle>

      <IonContent>
        <AnimationFill
          onComplete={onFinishAnimation}
          show
          steps={animationSteps}
          stepDuration={4}
          titleToItemsSpacing={16}
          itemsSpacing={32}
          progressStyles={{
            backgroundColor: '#262130',
          }}
        />

        <section className="setting-up-user-reviews">
          <UiTypography variant="title-large" align="center" transform="bold">
            Trusted by 1,556,867 users
          </UiTypography>
          <ReviewCardsCompatibility />
        </section>
      </IonContent>

      <OnboardingFooter
        disabled={disabledNextStep}
        onContinue={handleNextStep}
      />
    </ScreenSettingUpProfileStyles>
  );
};
